import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { AssignedReviewPeriod } from "src/store/api/generatedApi";

dayjs.extend(utc);

const STALE_THRESHOLD_HOURS = 2;

export interface AssignedRange {
  readonly start: string;
  readonly end: string;
  readonly hasStale: boolean;
  readonly earliestExpiresAt: string | null;
}

const isStale = (assignedAt: string | null): boolean => {
  if (!assignedAt) return false;
  return (
    dayjs.utc().diff(dayjs.utc(assignedAt), "hour") >= STALE_THRESHOLD_HOURS
  );
};

const expiresAt = (
  assignedAt: string | null,
  expirySeconds: number | undefined
): string | null =>
  assignedAt && expirySeconds != null
    ? dayjs.utc(assignedAt).add(expirySeconds, "second").toISOString()
    : null;

const earliestExpiry = (
  current: string | null,
  next: string | null
): string | null => {
  if (current === null) return next;
  if (next === null) return current;
  return next < current ? next : current;
};

export const mergeConsecutivePeriods = (
  periods: AssignedReviewPeriod[],
  expirySeconds: number | undefined
): readonly AssignedRange[] => {
  if (periods.length === 0) return [];

  const [first, ...rest] = [...periods].sort((a, b) =>
    a.start.localeCompare(b.start)
  );
  if (!first) return [];
  const ranges: AssignedRange[] = [];
  let currentStart = first.start;
  let currentEnd = first.end;
  let currentHasStale = isStale(first.assigned_at);
  let currentEarliestExpiry = expiresAt(first.assigned_at, expirySeconds);

  for (const period of rest) {
    if (period.start === currentEnd) {
      currentEnd = period.end;
      if (isStale(period.assigned_at)) currentHasStale = true;
      currentEarliestExpiry = earliestExpiry(
        currentEarliestExpiry,
        expiresAt(period.assigned_at, expirySeconds)
      );
    } else {
      ranges.push({
        start: currentStart,
        end: currentEnd,
        hasStale: currentHasStale,
        earliestExpiresAt: currentEarliestExpiry,
      });
      currentStart = period.start;
      currentEnd = period.end;
      currentHasStale = isStale(period.assigned_at);
      currentEarliestExpiry = expiresAt(period.assigned_at, expirySeconds);
    }
  }

  ranges.push({
    start: currentStart,
    end: currentEnd,
    hasStale: currentHasStale,
    earliestExpiresAt: currentEarliestExpiry,
  });

  return ranges;
};

export const getTotalAssignedMinutes = (
  ranges: readonly AssignedRange[]
): number =>
  ranges.reduce(
    (total, range) =>
      total + dayjs.utc(range.end).diff(dayjs.utc(range.start), "minute"),
    0
  );

export const hasAnyStaleRange = (ranges: readonly AssignedRange[]): boolean =>
  ranges.some((range) => range.hasStale);
